'use client';

import { ChevronLeft, Command, Shield } from 'lucide-react';
import Link from '@/components/ui/link';
import { useLocation } from 'react-router';

import {
  ADMIN_DESTINATIONS,
  destinationFromPath,
  destinationPathFor,
} from './admin-destinations';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession, signOut } from '@/lib/auth-client';
import { useIsLargeDesktop } from '@/hooks/use-large-desktop';
import { cn } from '@/lib/utils';

export interface AdminTopbarProps {
  /** Triggers the ⌘K command palette. */
  onOpenCommandPalette: () => void;
}

export function AdminTopbar({
  onOpenCommandPalette,
}: AdminTopbarProps): React.ReactElement {
  const location = useLocation();
  const session = useSession();
  const isLarge = useIsLargeDesktop();
  const active = destinationFromPath(location.pathname);
  const insideDestination = active && active.slug !== 'dashboard';

  const user = session.data?.user;
  const initial = (user?.username ?? user?.email ?? '?').slice(0, 1).toUpperCase();

  return (
    <header
      className={cn(
        'sticky top-0 z-40 border-b border-border/60 bg-background/70',
        'backdrop-blur-md'
      )}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-3 sm:px-6 lg:px-8">
        {/* Left: brand or back-chevron */}
        {isLarge || !insideDestination ? (
          <Link
            href="/admin"
            className="flex items-center gap-2 text-sm font-semibold tracking-tight"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/12 text-primary">
              <Shield className="h-4 w-4" />
            </span>
            <span className="hidden sm:inline">Admin</span>
          </Link>
        ) : (
          <Link
            href="/admin"
            className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="font-semibold text-foreground">
              {active?.label}
            </span>
          </Link>
        )}

        {/* Center: destination chips (desktop only) */}
        {isLarge && (
          <nav className="ml-2 flex flex-1 items-center gap-1.5">
            {ADMIN_DESTINATIONS.map((d) => {
              const path = destinationPathFor(d.slug);
              const isActive = active?.slug === d.slug;
              return (
                <Link
                  key={d.slug}
                  href={path}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-[13px] font-medium transition',
                    isActive
                      ? 'border border-primary/40 bg-primary/15 text-primary'
                      : 'border border-border/60 text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                  )}
                >
                  {d.label}
                </Link>
              );
            })}
          </nav>
        )}

        {/* Spacer for mobile */}
        {!isLarge && <div className="flex-1" />}

        {/* Right: ⌘K + avatar */}
        <button
          type="button"
          onClick={onOpenCommandPalette}
          className={cn(
            'flex items-center gap-1.5 rounded-md border border-border/60',
            'bg-background/40 px-2.5 py-1.5 text-xs text-muted-foreground',
            'hover:border-border hover:text-foreground'
          )}
        >
          <Command className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Search</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring/60"
              aria-label="Account menu"
            >
              <Avatar className="h-8 w-8">
                {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/">Exit Admin</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => void signOut()}
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
